/**
 * IntegratrAdapter.ts - IntegratrAdapter
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { LogLevel } from '../models/LogEntry';

export class IntegratrAdapter {
    options: Record<string, unknown>;
  integratr: any;

  constructor(options = {}) {
    this.options = {
      minLevel: options.minLevel !== undefined ? options.minLevel : LogLevel.INFO,
      integratr: options.integratr || null, // Integratr instance
      services: options.services || [], // Array of service names to send to
      ...options
    };
    
    this.integratr = this.options.integratr;
  }
  
  /**
   * Set Integratr instance
   */
  setIntegratr(integratr): any {
    this.integratr = integratr;
  }
  
  /**
   * Write log entry
   */
  async write(logEntry) {
    if (!logEntry.isLevelEnabled(this.options.minLevel)) {
      return;
    }
    
    if (!this.integratr) {
      console.warn('Integratr not configured for logging');
      return;
    }
    
    const logData = logEntry.toStructuredLog();
    
    // Send to each configured service
    for (const serviceName of this.options.services) {
      try {
        await this.sendToService(serviceName, logEntry, logData);
      } catch (error) {
        console.error(`Failed to send log to ${serviceName}:`, error);
      }
    }
  }
  
  /**
   * Send to specific service
   */
  async sendToService(serviceName, logEntry, logData) {
    switch (serviceName.toLowerCase()) {
      case 'datadog':
        await this.sendToDatadog(logEntry, logData);
        break;
      
      case 'sentry':
        await this.sendToSentry(logEntry, logData);
        break;
      
      case 'newrelic':
      case 'new-relic':
        await this.sendToNewRelic(logEntry, logData);
        break;
      
      case 'logrocket':
        await this.sendToLogRocket(logEntry, logData);
        break;
      
      case 'splunk':
        await this.sendToSplunk(logEntry, logData);
        break;
      
      default:
        // Generic integration
        await this.sendToGeneric(serviceName, logEntry, logData);
    }
  }
  
  /**
   * Send to Datadog
   */
  async sendToDatadog(logEntry, logData) {
    if (this.integratr.hasIntegration('datadog')) {
      await this.integratr.send('datadog', 'log', logData);
    }
  }
  
  /**
   * Send to Sentry
   */
  async sendToSentry(logEntry, logData) {
    if (this.integratr.hasIntegration('sentry')) {
      // Sentry expects errors
      if (logEntry.level >= LogLevel.ERROR && logEntry.error) {
        await this.integratr.send('sentry', 'error', {
          error: logEntry.error,
          level: logEntry.levelName.toLowerCase(),
          tags: logEntry.tags,
          extra: logData
        });
      }
    }
  }
  
  /**
   * Send to New Relic
   */
  async sendToNewRelic(logEntry, logData) {
    if (this.integratr.hasIntegration('newrelic')) {
      await this.integratr.send('newrelic', 'log', {
        message: logEntry.message,
        level: logEntry.levelName,
        timestamp: logEntry.timestamp,
        attributes: logData
      });
    }
  }
  
  /**
   * Send to LogRocket
   */
  async sendToLogRocket(logEntry, logData) {
    if (this.integratr.hasIntegration('logrocket')) {
      await this.integratr.send('logrocket', 'log', {
        level: logEntry.levelName.toLowerCase(),
        message: logEntry.message,
        data: logData
      });
    }
  }
  
  /**
   * Send to Splunk
   */
  async sendToSplunk(logEntry, logData) {
    if (this.integratr.hasIntegration('splunk')) {
      await this.integratr.send('splunk', 'event', {
        event: logData,
        time: new Date(logEntry.timestamp).getTime() / 1000,
        source: 'opcentral',
        sourcetype: '_json'
      });
    }
  }
  
  /**
   * Send to generic integration
   */
  async sendToGeneric(serviceName, logEntry, logData) {
    if (this.integratr.hasIntegration(serviceName)) {
      await this.integratr.send(serviceName, 'log', logData);
    }
  }
}

export default IntegratrAdapter;
